import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Header, Segment, Container, Radio, Button, Checkbox } from "semantic-ui-react";
import { NonWeightedUserResponseDto } from "../../../models/nonWeightedUserResponseDto";
import { toast } from "react-toastify";
import parse from "html-react-parser";
import agent from "../../../api/agent";
import { useStore } from "../../../stores/store";
import { StartUserSurveyDto } from "../../../models/startUserSurveyDto";

interface Props {
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
}

export default function PreSurveyAboutMePage({ onNavigateNext, onNavigatePrevious }: Props) {
  const { commonStore } = useStore();
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState<boolean>(false);
  const [schoolLocation, setSchoolLocation] = useState<number>(-1);
  const [gender, setGender] = useState<number>(-1);
  const [studentsRange, setStudentsRange] = useState<number[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<number>(-1);
  const [teachingExperience, setTeachingExperience] = useState<number>(-1);
  const [digitalExperience, setDigitalExperience] = useState<number>(-1);
  const [previousTieseaTeacher, setPreviousTieseaTeacher] = useState<number>(-1);

  const schoolLocationsList: { text: string; value: number }[] = [
    { text: t("AboutMe.low"), value: 0 },
    { text: t("AboutMe.semi"), value: 1 },
    { text: t("AboutMe.high"), value: 2 },
  ];

  const genderList: { text: string; value: number }[] = [
    { text: t("AboutMe.Male"), value: 0 },
    { text: t("AboutMe.Female"), value: 1 },
    //{ text: t("AboutMe.Other"), value: 2 },
    { text: t("AboutMe.Prefer not to say"), value: 3 },
  ];

  const subjectsList: { text: string; value: number }[] = [
    {
      text: t("AboutMe.Q3Op0"),
      value: 1,
    },
    {
      text: t("AboutMe.Q3Op1"),
      value: 2,
    },
    {
      text: t("AboutMe.Q3Op2"),
      value: 3,
    },
    {
      text: t("AboutMe.Q3Op3"),
      value: 4,
    },
    {
      text: t("AboutMe.Q3Op4"),
      value: 5,
    },
    {
      text: t("AboutMe.Q3Op5"),
      value: 6,
    },
    {
      text: t("AboutMe.Q3Op6"),
      value: 7,
    },
    {
      text: t("AboutMe.Q3Op7"),
      value: 8,
    },
    { text: t("AboutMe.Q3Op8"), value: 9 },
    {
      text: t("AboutMe.Q3Op9"),
      value: 10,
    },
    {
      text: t("AboutMe.Q3Op10"),
      value: 11,
    },
  ];

  const studentRangeValues: string[] = [
    //t("AboutMe.Q4Op1"),
    t("AboutMe.Q4Op2"),
    t("AboutMe.Q4Op3"),
    t("AboutMe.Q4Op4"),
    t("AboutMe.Q4Op5"),
    //t("AboutMe.Q4Op6"),
    //t("AboutMe.Q4Op7"),
  ];

  const experienceYearsList: { text: string; value: number }[] = [
    { text: t("AboutMe.Q5Op1"), value: 0 },
    { text: t("AboutMe.Q5Op2"), value: 1 },
    { text: t("AboutMe.Q5Op3"), value: 2 },
    { text: t("AboutMe.Q5Op4"), value: 3 },
    { text: t("AboutMe.Q5Op5"), value: 4 },
  ];

  const digitalExperienceList: { text: string; value: number }[] = [
    { text: t("AboutMe.Q6Op1"), value: 0 },
    { text: t("AboutMeKH.Q6Op2"), value: 1 },
    { text: t("AboutMe.Q6Op3"), value: 2 },
    { text: t("AboutMe.Q6Op4"), value: 3 },
    { text: t("AboutMe.Q6Op5"), value: 4 },
  ];

  const addRemoveSelection = (list: number[], range: number): number[] => {
    let index = list.indexOf(range);
    if (index != -1) {
      let array = [...list];
      array = list.filter((r) => r != range);
      return array;
    } else {
      let array = [...list];
      array.push(range);
      return array;
    }
  };

  const handleError = (error: any) =>
    toast.error(
      "An error has occurred while processing your request. Please try again or contact your system admin"
    );

  useEffect(() => {
    //initiate user survey
    if (commonStore.currentUserSurveyId != 0) return;

    let dto: StartUserSurveyDto = {
      email: "",
      phoneNumber: "",
      userSurveyId: 0,
      telegramUserName: "",
    };

    if (commonStore.currentSurveyCode == null) {
      toast.error("Invalid survey code !");
      return;
    }

    agent.UserSurveys.start(commonStore.currentSurveyCode, dto)
      .then((result) => {
        commonStore.setCurrentUserSurveyId(result);
      })
      .catch((error) => {
        handleError(error);
      });
  }, []);

  const saveAboutMe = () => {
    if (commonStore.currentUserSurveyId === 0) {
      toast.warn("Still setting up survey, please try again");
      return;
    }

    setLoading(true);

    let question0: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 0,
      responseId: previousTieseaTeacher,
      response: previousTieseaTeacher === 1 ? "Yes" : "No",
      hasMultipleResponses: false,
      responseValues: null,
      question: "Have you been trained under the TIESEA project?",
    };

    let question1: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 1,
      responseId: schoolLocation,
      hasMultipleResponses: false,
      responseValues: null,
      question: "Where is your school located?",
    };

    let question2: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 2,
      responseId: gender,
      hasMultipleResponses: false,
      responseValues: null,
      question: "Which one of the following best describes your gender?",
    };

    let question3: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 3,
      responseId: selectedSubject,
      hasMultipleResponses: false,
      responseValues: null,
      question: "Which of the following subjects do you teach?",
    };

    let question4: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 4,
      responseId: gender,
      hasMultipleResponses: true,
      responseValues: studentsRange,
      question: "What is the age range of your students?",
    };

    let question5: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 5,
      responseId: teachingExperience,
      hasMultipleResponses: false,
      responseValues: null,
      question: "How many years have you been teaching?",
    };

    let question6: NonWeightedUserResponseDto = {
      id: 0,
      userSurveyId: commonStore.currentUserSurveyId,
      questionNumber: 6,
      responseId: digitalExperience,
      hasMultipleResponses: false,
      responseValues: null,
      question: "For how long have you been using digital technologies in teaching?",
    };

    let dtos: NonWeightedUserResponseDto[] = [
      question0,
      question1,
      question2,
      question3,
      question4,
      question5,
      question6,
    ];

    agent.UserSurveys.savePreSurveyQuestions(dtos)
      .then((result) => {
        onNavigateNext();
      })
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1">{t("AboutMe.AboutMe")} </Header>
      </Segment>
      <div className="pre-post-txt">
        <Container>
          <div className="question-wrapper">
            <Segment className="survey-question">{t("AboutMe.Q0-AreYouPreviousTieseaTeacher")}</Segment>
            <Segment className="survey-answers">
              <Radio
                onChange={() => setPreviousTieseaTeacher(1)}
                checked={previousTieseaTeacher === 1}
                label={t("Common.Yes")}
              />
              <Radio
                onChange={() => setPreviousTieseaTeacher(0)}
                checked={previousTieseaTeacher === 0}
                label={t("Common.No")}
              />
            </Segment>
          </div>
          <div className="question-wrapper">
            <Segment className="survey-question">{t("AboutMe.Q1")} </Segment>
            <Segment className="survey-answers">
              {schoolLocationsList &&
                schoolLocationsList.length > 0 &&
                schoolLocationsList.map((location, index) => (
                  <Radio
                    key={index}
                    onChange={() => setSchoolLocation(location.value)}
                    checked={schoolLocation === location.value}
                    label={location.text}
                  />
                ))}
            </Segment>
          </div>
          <div className="question-wrapper">
            <Segment className="survey-question">{t("AboutMe.Q2")} </Segment>
            <Segment className="survey-answers">
              {genderList &&
                genderList.length > 0 &&
                genderList.map((item, index) => (
                  <Radio
                    key={index}
                    onChange={() => setGender(item.value)}
                    checked={gender === item.value}
                    label={item.text}
                  />
                ))}
            </Segment>
          </div>
          <div className="question-wrapper">
            <Segment className="survey-question">
              {t("AboutMe.Q3")}
              {/* <i> {t("AboutMe.Q3Sub")} </i> */}
            </Segment>
            <Segment className="survey-answers">
              {subjectsList.map((item, index) => (
                <Radio
                  key={index}
                  label={parse(item.text)}
                  checked={selectedSubject === item.value}
                  onClick={() => {
                    setSelectedSubject(item.value);
                  }}
                />
              ))}
            </Segment>
          </div>
          <div className="question-wrapper">
            <Segment className="survey-question">
              {t("AboutMe.Q4")}
              <i> {t("AboutMe.Q4Sub")} </i>
            </Segment>
            <Segment className="survey-answers">
              {studentRangeValues.map((range, index) => (
                <Checkbox
                  key={index}
                  label={range}
                  checked={studentsRange.filter((r) => r === index).length > 0}
                  onClick={() => {
                    let array = addRemoveSelection(studentsRange, index);
                    setStudentsRange(array);
                  }}
                />
              ))}
            </Segment>
          </div>
          <div className="question-wrapper">
            <Segment className="survey-question">{t("AboutMe.Q5")} </Segment>
            <Segment className="survey-answers">
              {experienceYearsList &&
                experienceYearsList.length > 0 &&
                experienceYearsList.map((item, index) => (
                  <Radio
                    key={index}
                    onChange={() => setTeachingExperience(item.value)}
                    checked={teachingExperience === item.value}
                    label={item.text}
                  />
                ))}
            </Segment>
          </div>
          <div className="question-wrapper">
            <Segment className="survey-question">{t("AboutMe.Q6")}</Segment>
            <Segment className="survey-answers">
              {digitalExperienceList &&
                digitalExperienceList.length > 0 &&
                digitalExperienceList.map((item, index) => (
                  <Radio
                    key={index}
                    onChange={() => setDigitalExperience(item.value)}
                    checked={digitalExperience === item.value}
                    label={item.text}
                  />
                ))}
            </Segment>
          </div>
        </Container>
      </div>
      <Container className="survey-btns">
        <Segment>
          <Button
            primary
            disabled={loading}
            floated="left"
            onClick={() => {
              onNavigatePrevious();
            }}
          >
            {t("Common.Previous")}
          </Button>

          <Button
            primary
            floated="right"
            loading={loading}
            disabled={
              schoolLocation === -1 ||
              gender === -1 ||
              digitalExperience === -1 ||
              teachingExperience === -1 ||
              selectedSubject === -1 ||
              previousTieseaTeacher === -1 ||
              studentsRange.length === 0
            }
            onClick={() => {
              saveAboutMe();
            }}
          >
            {t("Common.Next")}
          </Button>
        </Segment>
      </Container>
    </>
  );
}
